import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
	Accordion,
	Button,
	Checkbox,
	type CheckboxProps,
	Dropdown,
	type DropdownProps,
	Icon,
	Input,
	type InputProps,
	Popup,
} from "semantic-ui-react";

import { useQuery } from "@tanstack/react-query";

import { getProtocolTrialFilters } from "../../../apis/protocolApi";
import type { ProtocolDashboardOptions, ProtocolTreatment } from "../../../apis/types/protocolTypes";
import type { ProtocolTimeSeriesPreset } from "../../../apis/types/analysisTypes";
import { useAuth0 } from "../../../auth/auth0";
import { useUserAuth } from "../../../hooks/useUserAuth";
import { toDropdownItems } from "../../../utilities/dropdownOptions";
import {
	AnalysisDisplaySettings,
	AnalysisSettings,
	useProtocolAnalysis,
	useProtocolAnalysisDispatch,
} from "../../Content/Protocols/ProtocolAnalysis/ProtocolAnalysisContext";
import type { AnalysisResultForOptions, TimecourseSelection } from "../../Content/Protocols/ProtocolAnalysis/utils";
import {
	assessmentUsesPlannedTiming,
	findMatchingTimecoursePreset,
	getAlphaOptions,
	getAssessmentOptions,
	getDataNormalizationOptions,
	getDefaultQuantifiedRegionId,
	getDefaultTimecourseAnalysisId,
	getDefaultTrialFilter,
	getMeanComparisonOptions,
	getPlannedTimingOptions,
	getPlannedTimingOptionsForAssessment,
	getQuantifiedRegionOptions,
	getTimecourseAnalysisTypeOptions,
	getTimecourseOrthoOptions,
	getTimecoursePresetOptions,
	getTrialFilterOptions,
	isUniformityAnalysis,
	resolveAssessmentSelection,
	resolveTimecoursePresetSelection,
	resolveTimecourseSelection,
} from "../../Content/Protocols/ProtocolAnalysis/utils";

import styles from "./styles.module.css";

type Mode = "timecourse" | "boxWhisker" | "piano" | "summaryTable";

const removeExclusionsInfo =
	"When toggled to the 'on' position any regions the user has previously marked as excluded will be discarded from analyses";
const meanComparisonInfo =
	"A statistical test or p-value adjustment that evaluates differences among all pairs of treatments";

const MODE_NAMES: Record<Mode, string> = {
	timecourse: "Timecourse",
	boxWhisker: "Box Whisker",
	piano: "Piano Charts",
	summaryTable: "Summary Table",
};

/** The timecourse x-axis. Values are the legacy "planned timing" ids the DAP/GDU dropdown used. */
const DAP = 1;
const GDU = 2;
const timecourseAxisOptions = [
	{ key: DAP, value: DAP, text: "DAP" },
	{ key: GDU, value: GDU, text: "GDU" },
];

export const DropdownWithLabel = (props: DropdownProps & { label: React.ReactNode; info?: React.ReactNode }) => {
	const { label, info, options, ...rest } = props;
	// Options carry domain fields (ex: analysisId) that Dropdown would spread onto DOM elements
	const dropdownItems = useMemo(() => toDropdownItems(options), [options]);
	return (
		<div>
			<label style={{ display: "flex", gap: 6 }}>
				{label}
				{!!info && (
					<Popup
						offset={[-11]}
						wide={"very"}
						trigger={<Icon style={{ margin: 0, verticalAlign: "top" }} name="info circle" />}
						on={"hover"}
						content={info}
					/>
				)}
			</label>
			<Dropdown selection fluid options={dropdownItems ?? []} {...rest} />
		</div>
	);
};

const ToggleWithLabel = (props: CheckboxProps & { label: string; info?: string }) => {
	const { label, info, ...rest } = props;
	return (
		<div>
			<label htmlFor={props.id as string | undefined} style={{ display: "unset" }}>
				{label}
				{!!info && (
					<Popup
						offset={[-11]}
						trigger={<Icon style={{ marginLeft: 10, verticalAlign: "top" }} name="info circle" />}
						on={"hover"}
						content={info}
					/>
				)}
			</label>
			<Checkbox style={{ height: "38px", display: "flex", alignItems: "center" }} toggle {...rest} label={undefined} />
		</div>
	);
};

const InputWithLabel = (props: InputProps & { label: React.ReactNode }) => {
	const { label, ...rest } = props;
	return (
		<div>
			<label style={{ display: "flex", gap: 6 }}>{label}</label>
			<Input className={styles.rangeInput} fluid {...rest} />
		</div>
	);
};

type Props = {
	mode?: Mode;
	dashboardOptions?: ProtocolDashboardOptions;
	/** The protocol's treatments — narrows which data normalizations are offered. */
	treatments?: ProtocolTreatment[];
	/**
	 * Timecourse only. The fetched plot analysis results, which are where the Assessment, Analysis
	 * and Quantified Region dropdowns come from — unlike every other control on this bar, those
	 * three are not in ProtocolDashboardOptions.
	 */
	analysisResults?: AnalysisResultForOptions[];
	/**
	 * Timecourse only. `presetOptions.timeSeriesPresets` from the protocol timecourse response.
	 * A preset is the unit of selection: it resolves analysisId, analysisTypeId and
	 * quantifiedRegionTypeId together.
	 */
	timeSeriesPresets?: ProtocolTimeSeriesPreset[];
	/** Client-wide analyses (analysisActions.getAnalyses `data`), source of the Stand Count option. */
	standCountAnalyses?: { id: string; name: string; analysisTypeId: string }[];
	/** Disables every control, e.g. while a mean comparison is running. */
	disabled?: boolean;
	loading?: boolean;
	/**
	 * Runs instead of commitSettings() when Update Analytics is clicked. Pages use this to validate
	 * first — the timecourse and box whisker both require at least two trials and two treatments.
	 */
	onCommit?: () => void;
	/**
	 * Page-specific controls rendered at the start of the bar, e.g. the summary table's editor
	 * button and preset dropdown. Use DropdownWithLabel to match the rest of the bar.
	 */
	leadingControls?: React.ReactNode;
};

const ProtocolAnalysisTopBar = ({
	mode = "timecourse",
	dashboardOptions,
	treatments,
	analysisResults,
	timeSeriesPresets,
	standCountAnalyses,
	disabled = false,
	loading = false,
	onCommit,
	leadingControls,
}: Props) => {
	const userAuth = useUserAuth();
	const { getTokenSilently } = useAuth0();

	const { pending, display, isDirty, isInitialized } = useProtocolAnalysis();
	const { changeSettings, changeSettingsSilently, changeDisplaySettings, commitSettings, initializeSettings } =
		useProtocolAnalysisDispatch();

	const [accordionActive, setAccordionActive] = useState(false);
	// Start/End are staged locally so typing does not mark the analysis dirty on every keystroke;
	// they are pushed into pending settings on blur, once validated against each other.
	const [stagedStart, setStagedStart] = useState<string>();
	const [stagedEnd, setStagedEnd] = useState<string>();

	const isTimecourse = mode === "timecourse";
	const isPiano = mode === "piano";
	// Each summary table column carries its own assessment and normalization, so the page-level
	// Assessment, Growth Phase and Data Normalization dropdowns do not apply there
	const isSummaryTable = mode === "summaryTable";

	/* ----------------------------- dropdown options ----------------------------- */

	// Every tab's assessments, to name one chosen on another tab that this one lacks
	const allAssessmentOptions = useMemo(
		() => (dashboardOptions ? getAssessmentOptions(dashboardOptions, standCountAnalyses) : undefined),
		[dashboardOptions, standCountAnalyses],
	);

	// The piano charts have no stand count view
	const assessmentOptions = useMemo(
		() => (isPiano ? _.filter(allAssessmentOptions, (o) => o.kind !== "standcount") : allAssessmentOptions),
		[allAssessmentOptions, isPiano],
	);

	const plannedTimingOptions = useMemo(
		() => (dashboardOptions ? getPlannedTimingOptions(dashboardOptions.groundDataAssessments) : undefined),
		[dashboardOptions],
	);

	const plannedTimingOptionsForAssessment = useMemo(
		() =>
			plannedTimingOptions ? getPlannedTimingOptionsForAssessment(plannedTimingOptions, pending.assessmentValue) : [],
		[plannedTimingOptions, pending.assessmentValue],
	);

	const dataNormalizationOptions = useMemo(
		() =>
			dashboardOptions
				? getDataNormalizationOptions(dashboardOptions.dataNormalizations, treatments, {
						excludeNonTreatmentTypes: isPiano,
					})
				: undefined,
		[dashboardOptions, treatments, isPiano],
	);

	const meanComparisonOptions = useMemo(
		() => (dashboardOptions ? getMeanComparisonOptions(dashboardOptions.meanComparisons) : undefined),
		[dashboardOptions],
	);

	const alphaOptions = useMemo(
		() => (dashboardOptions ? getAlphaOptions(dashboardOptions.alphas) : undefined),
		[dashboardOptions],
	);

	const timecoursePresetOptions = useMemo(
		() =>
			analysisResults?.length && timeSeriesPresets?.length
				? getTimecoursePresetOptions(timeSeriesPresets, analysisResults)
				: undefined,
		[analysisResults, timeSeriesPresets],
	);

	const analysisTypeOptions = useMemo(
		() => (analysisResults?.length ? getTimecourseAnalysisTypeOptions(analysisResults) : undefined),
		[analysisResults],
	);

	// The ortho is not stored: several analyses share one, but the selected analysis has just one
	const selectedOrthoImageTypeId = useMemo(
		() => _.find(analysisResults, (r) => r.analysisId === pending.analysisId)?.orthoImageTypeId ?? undefined,
		[analysisResults, pending.analysisId],
	);

	const orthoOptions = useMemo(
		() => (analysisResults?.length ? getTimecourseOrthoOptions(analysisResults, pending.analysisTypeId) : undefined),
		[analysisResults, pending.analysisTypeId],
	);

	// Only the regions that exist for the selected analysis type and ortho, for the dropdown
	const quantifiedRegionDropdownOptions = useMemo(
		() =>
			analysisResults?.length
				? getQuantifiedRegionOptions(analysisResults, {
						analysisTypeId: pending.analysisTypeId,
						orthoImageTypeId: selectedOrthoImageTypeId,
					})
				: undefined,
		[analysisResults, pending.analysisTypeId, selectedOrthoImageTypeId],
	);

	// The Assessment dropdown follows the analysis type, ortho and quantified region dropdowns. Derived
	// rather than stored: clearing timecoursePresetId would make the seeding effect below treat the
	// selection as unseeded and reset it.
	const selectedTimecoursePresetId = useMemo(
		() => findMatchingTimecoursePreset(timecoursePresetOptions, pending, pending.timecoursePresetId)?.value,
		[timecoursePresetOptions, pending],
	);

	const { data: trialFilters, isLoading: trialFiltersLoading } = useQuery({
		// Same key the pages use, so this shares one fetch with them rather than adding a request
		queryKey: ["protocol trial filters", userAuth.currentClientId],
		queryFn: async () => getProtocolTrialFilters(userAuth.currentClientId ?? "", await getTokenSilently()),
		select: (data) => data.data,
		enabled: !isPiano && !!userAuth.currentClientId,
	});

	const trialFilterOptions = useMemo(() => getTrialFilterOptions(trialFilters), [trialFilters]);

	/* ------------------------------- initialization ------------------------------ */

	// Seeds the statistics settings from the protocol's defaults. These apply to every tab and
	// initializeSettings writes both pending and committed, so `isInitialized` gates it to once per
	// protocol rather than overwriting the user's choices.
	useEffect(() => {
		if (!dashboardOptions || !dataNormalizationOptions?.length || isInitialized) return;

		const defaultNormalization =
			_.find(dataNormalizationOptions, (o) => !o.disabled && o.text.toUpperCase() === "% MEAN") ??
			_.find(dataNormalizationOptions, (o) => !o.disabled) ??
			dataNormalizationOptions[0];

		initializeSettings({
			dataNormalizationId: defaultNormalization?.value ?? null,
			meanComparisonOptionId: dashboardOptions.defaultMeanComparisonId ?? meanComparisonOptions?.[0]?.value ?? null,
			alphaOptionId: dashboardOptions.defaultAlphaId ?? alphaOptions?.[0]?.value ?? null,
		});

		changeDisplaySettings({
			trialIds: dashboardOptions.trialIds,
			treatmentNames: _.map(treatments, (t) => t.name ?? ""),
		});
	}, [
		dashboardOptions,
		dataNormalizationOptions,
		meanComparisonOptions,
		alphaOptions,
		treatments,
		isInitialized,
		initializeSettings,
		changeDisplaySettings,
	]);

	// Seeds the assessment for every tab but the timecourse, which picks a time series preset in the
	// effect below instead. The box whisker, piano and summary table share one assessment; a tab that
	// lacks it shows its first assessment instead, and says so. The timecourse's presets are never the
	// same assessment, so it neither follows nor sets this one.
	//
	// Gated on the stored assessment and its ids rather than on `isInitialized`: the timecourse
	// latches isInitialized while choosing no assessment, and overwrites analysisId with its own.

	useEffect(() => {
		if (isTimecourse || !assessmentOptions?.length || !dataNormalizationOptions?.length) return;

		const current = _.find(assessmentOptions, (o) => o.value === pending.assessmentValue);
		const assessment = current ?? assessmentOptions[0];

		// Re-resolved, keeping the assessment, when only the ids are off after a timecourse visit
		const isAssessmentSelectionValid =
			!!current && current.analysisId === pending.analysisId && current.analysisTypeId === pending.analysisTypeId;

		const isDataNormalizationValid = _.some(
			dataNormalizationOptions,
			(dno) => pending.dataNormalizationId === dno.value && !dno.disabled,
		);

		// Only really want to call the setters once per effect so use objects to batch them
		let valuesToInitialize: Partial<AnalysisSettings> = {};
		let displayValuesToChange: Partial<AnalysisDisplaySettings> = {};

		if (!isAssessmentSelectionValid) {
			const growthPhases = getPlannedTimingOptionsForAssessment(plannedTimingOptions ?? [], assessment.value);

			valuesToInitialize = {
				...valuesToInitialize,
				...resolveAssessmentSelection(assessment),
				// a kept ground data assessment keeps its growth phase too
				plannedTimingId: assessmentUsesPlannedTiming(assessment.kind)
					? (_.find(growthPhases, (o) => o.value === pending.plannedTimingId)?.value ?? growthPhases[0]?.value ?? null)
					: null,
			};
			displayValuesToChange = { ...displayValuesToChange, assessmentName: assessment.text };

			// The summary table does not show the page-level assessment
			const missing = _.find(allAssessmentOptions, (o) => o.value === pending.assessmentValue);
			if (!current && missing && !isSummaryTable) {
				toast.info(`${missing.text} isn't available on ${MODE_NAMES[mode]}, showing ${assessment.text}`, {
					// effects run twice in strict mode
					toastId: "assessment-fallback",
				});
			}
		}
		if (!pending.dataNormalizationId || !isDataNormalizationValid) {
			valuesToInitialize = {
				...valuesToInitialize,
				dataNormalizationId: dataNormalizationOptions[0].value,
			};
		}

		initializeSettings(valuesToInitialize);
		changeDisplaySettings(displayValuesToChange);
	}, [
		isTimecourse,
		isSummaryTable,
		mode,
		assessmentOptions,
		allAssessmentOptions,
		plannedTimingOptions,
		pending.assessmentValue,
		initializeSettings,
		changeDisplaySettings,
		pending.analysisId,
		pending.analysisTypeId,
		pending.plannedTimingId,
		dataNormalizationOptions,
		pending.dataNormalizationId,
	]);

	// The timecourse's assessment defaults depend on fetched results, so they land after the
	// settings above. Restores the timecourse's own last selection, which the other tabs overwrite;
	// otherwise picks the preset covering the default analysis (Aerial NDVI where two or more trials
	// have it), then bare analysis + quantified region ids when the protocol has no matching preset.
	//
	// Gated on the remembered selection rather than on `timecoursePresetId`: a hand-picked analysis
	// type, ortho and region need not match a preset.
	useEffect(() => {
		if (!isTimecourse || !analysisResults?.length) return;

		const remembered = _.find(
			analysisResults,
			(r) =>
				r.analysisId === pending.timecourseAnalysisId &&
				r.analysisTypeId === pending.timecourseAnalysisTypeId &&
				r.quantifiedRegionTypeId === pending.quantifiedRegionTypeId,
		);
		if (remembered) {
			if (pending.analysisId === remembered.analysisId && pending.analysisTypeId === remembered.analysisTypeId) return;

			// Another tab left its assessment's ids behind
			const preset = findMatchingTimecoursePreset(timecoursePresetOptions, remembered, pending.timecoursePresetId);
			initializeSettings({ analysisId: remembered.analysisId, analysisTypeId: remembered.analysisTypeId });
			changeDisplaySettings({
				analysisName: remembered.analysisName,
				assessmentName: preset?.text ?? remembered.analysisName,
			});
			return;
		}

		// Only once there was a selection to lose
		const notifyFallback = (shown: string | null) => {
			if (!pending.timecourseAnalysisId) return;
			toast.info(`Your previous selection isn't available on the Timecourse, showing ${shown}`, {
				// effects run twice in strict mode
				toastId: "timecourse-fallback",
			});
		};

		const defaultAnalysisId = getDefaultTimecourseAnalysisId(analysisResults);
		const defaultPreset =
			_.find(timecoursePresetOptions, (o) => o.analysisId === defaultAnalysisId) ?? timecoursePresetOptions?.[0];

		if (defaultPreset) {
			initializeSettings({
				timecoursePresetId: defaultPreset.value,
				...resolveTimecoursePresetSelection(defaultPreset, dashboardOptions?.trialAnalysisOptions ?? []),
				timecourseAnalysisId: defaultPreset.analysisId,
				timecourseAnalysisTypeId: defaultPreset.analysisTypeId,
			});
			changeDisplaySettings({ assessmentName: defaultPreset.text });
			notifyFallback(defaultPreset.text);
			return;
		}

		// No preset covers this protocol, so timecoursePresetId stays null
		const defaultRegionId = getDefaultQuantifiedRegionId(analysisResults);
		const defaultRow =
			_.find(
				analysisResults,
				(r) => r.analysisId === defaultAnalysisId && r.quantifiedRegionTypeId === defaultRegionId,
			) ?? _.find(analysisResults, (r) => r.analysisId === defaultAnalysisId);
		if (!defaultRow) return;

		initializeSettings({
			analysisId: defaultRow.analysisId,
			analysisTypeId: defaultRow.analysisTypeId,
			quantifiedRegionTypeId: defaultRow.quantifiedRegionTypeId,
			isPlotUniformity: isUniformityAnalysis(dashboardOptions?.trialAnalysisOptions ?? [], defaultRow.analysisTypeId),
			timecourseAnalysisId: defaultRow.analysisId,
			timecourseAnalysisTypeId: defaultRow.analysisTypeId,
		});
		changeDisplaySettings({ analysisName: defaultRow.analysisName, assessmentName: defaultRow.analysisName });
		notifyFallback(defaultRow.analysisName);
	}, [
		pending.timecoursePresetId,
		isTimecourse,
		analysisResults,
		timecoursePresetOptions,
		dashboardOptions,
		pending.analysisId,
		pending.analysisTypeId,
		pending.timecourseAnalysisId,
		pending.timecourseAnalysisTypeId,
		initializeSettings,
		changeDisplaySettings,
		pending.quantifiedRegionTypeId,
	]);

	useEffect(() => {
		if (display.trialFilterId !== null) return;
		const defaultTrialFilter = getDefaultTrialFilter(trialFilterOptions);
		if (defaultTrialFilter) {
			changeDisplaySettings({ trialFilterId: defaultTrialFilter.value, trialFilters: defaultTrialFilter.filters });
		}
	}, [trialFilterOptions, display.trialFilterId, changeDisplaySettings]);

	// Keep the staged range inputs in step with committed values (e.g. after a reset).
	useEffect(() => {
		setStagedStart(String(pending.timecourseStart));
		setStagedEnd(String(pending.timecourseEnd));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isInitialized]);

	/* --------------------------------- handlers --------------------------------- */

	const handleAssessmentChange = (value: string) => {
		const option = _.find(assessmentOptions, (o) => o.value === value);
		if (!option) return;

		const plannedTimingId = assessmentUsesPlannedTiming(option.kind)
			? (getPlannedTimingOptionsForAssessment(plannedTimingOptions ?? [], value)[0]?.value ?? null)
			: null;

		changeSettingsSilently({ ...resolveAssessmentSelection(option), plannedTimingId });
		changeDisplaySettings({ assessmentName: option.text });
	};

	const handleTimecoursePresetChange = (value: string) => {
		const preset = _.find(timecoursePresetOptions, (o) => o.value === value);
		if (!preset) return;

		changeSettingsSilently({
			timecoursePresetId: preset.value,
			...resolveTimecoursePresetSelection(preset, dashboardOptions?.trialAnalysisOptions ?? []),
			timecourseAnalysisId: preset.analysisId,
			timecourseAnalysisTypeId: preset.analysisTypeId,
		});
		changeDisplaySettings({ assessmentName: preset.text });
	};

	// Shared by the Analysis Type, Ortho and Quantified Region dropdowns. Whichever changed is passed
	// in; the rest are kept where they still fit. The analysis type decides the uniformity flag, so it
	// is recomputed here, and the preset name is shown while one matches.
	const handleTimecourseSelectionChange = (change: Partial<TimecourseSelection>) => {
		const selection = resolveTimecourseSelection(analysisResults ?? [], {
			analysisTypeId: pending.analysisTypeId,
			orthoImageTypeId: selectedOrthoImageTypeId ?? null,
			quantifiedRegionTypeId: pending.quantifiedRegionTypeId,
			...change,
		});
		if (!selection) return;

		const preset = findMatchingTimecoursePreset(timecoursePresetOptions, selection, pending.timecoursePresetId);

		changeSettingsSilently({
			analysisId: selection.analysisId,
			analysisTypeId: selection.analysisTypeId,
			quantifiedRegionTypeId: selection.quantifiedRegionTypeId,
			isPlotUniformity: isUniformityAnalysis(dashboardOptions?.trialAnalysisOptions ?? [], selection.analysisTypeId),
			timecourseAnalysisId: selection.analysisId,
			timecourseAnalysisTypeId: selection.analysisTypeId,
		});
		changeDisplaySettings({
			analysisName: selection.analysisName,
			assessmentName: preset?.text ?? selection.analysisName,
		});
	};

	const handleStartBlur = () => {
		const next = Number(stagedStart);
		if (Number.isNaN(next) || next === pending.timecourseStart) return;

		if (next > Number(pending.timecourseEnd)) {
			toast.error("Start Day needs to be less than or equal to End Day");
			return;
		}
		changeSettings({ timecourseStart: next });
	};

	const handleEndBlur = () => {
		const next = Number(stagedEnd);
		if (Number.isNaN(next) || next === pending.timecourseEnd) return;

		if (next < Number(pending.timecourseStart)) {
			toast.error("End Day needs to be greater than or equal to Start Day");
			return;
		}
		changeSettings({ timecourseEnd: next });
	};

	/* ---------------------------------- render ---------------------------------- */

	// GDUs need a plant date on every trial and a GDU formula for the crop
	const gduAvailable = display.allTrialsHavePlantDate && display.hasGduFormula;
	const rangeDisabled = disabled || loading || !gduAvailable;
	const controlsDisabled = disabled || loading;

	return (
		<div className={styles.topBarContainer}>
			<Accordion fluid>
				<Accordion.Title active={accordionActive}>
					<div className={styles.controlRow}>
						{isTimecourse && (
							<div
								onClick={() => setAccordionActive((prev) => !prev)}
								style={{ fontSize: "1.28571429rem", fontWeight: 700, cursor: "pointer" }}
							>
								<Icon name="dropdown" style={{ margin: 0 }} />
							</div>
						)}
						{leadingControls}
						{isTimecourse ? (
							<DropdownWithLabel
								label="Assessment"
								loading={timecoursePresetOptions === undefined}
								disabled={controlsDisabled}
								options={timecoursePresetOptions ?? []}
								// "" rather than undefined: an undefined value leaves the dropdown uncontrolled, so it
								// would keep showing the last preset clicked once none matches
								value={selectedTimecoursePresetId ?? ""}
								onChange={(_event, { value }) => handleTimecoursePresetChange(value as string)}
							/>
						) : isSummaryTable ? null : (
							<DropdownWithLabel
								label="Assessment"
								loading={assessmentOptions === undefined}
								disabled={controlsDisabled}
								options={assessmentOptions ?? []}
								value={pending.assessmentValue ?? undefined}
								onChange={(_event, { value }) => handleAssessmentChange(value as string)}
							/>
						)}
						{!isTimecourse && !isSummaryTable && (
							<DropdownWithLabel
								label={isPiano ? "Planned Timing" : "Growth Phase"}
								loading={plannedTimingOptions === undefined}
								disabled={controlsDisabled || !assessmentUsesPlannedTiming(pending.assessmentKind)}
								options={plannedTimingOptionsForAssessment}
								value={pending.plannedTimingId ?? undefined}
								onChange={(_event, { value }) => changeSettingsSilently({ plannedTimingId: value as string })}
							/>
						)}
						{!isSummaryTable && (
							<DropdownWithLabel
								label={isTimecourse ? "Normalization" : "Data Normalization"}
								loading={dataNormalizationOptions === undefined}
								disabled={controlsDisabled}
								options={dataNormalizationOptions ?? []}
								value={pending.dataNormalizationId ?? undefined}
								onChange={(_event, { value }) => changeSettings({ dataNormalizationId: value as string })}
							/>
						)}
						{isTimecourse && (
							<>
								<DropdownWithLabel
									label="Timecourse"
									disabled={rangeDisabled}
									options={gduAvailable ? timecourseAxisOptions : [timecourseAxisOptions[0]]}
									value={pending.useGdu ? GDU : DAP}
									onChange={(_event, { value }) => {
										changeSettings({ useGdu: value === GDU });
										changeDisplaySettings({ hideLineCharts: true });
									}}
								/>
								<InputWithLabel
									label="Start"
									type="text"
									disabled={rangeDisabled}
									value={stagedStart ?? ""}
									onChange={(event) => setStagedStart(event.target.value)}
									onBlur={handleStartBlur}
								/>
								<InputWithLabel
									label="End"
									type="text"
									disabled={rangeDisabled}
									value={stagedEnd ?? ""}
									onChange={(event) => setStagedEnd(event.target.value)}
									onBlur={handleEndBlur}
								/>
							</>
						)}
						{!isPiano && (
							<>
								<DropdownWithLabel
									label="Mean Comparison"
									info={meanComparisonInfo}
									loading={meanComparisonOptions === undefined}
									disabled={controlsDisabled}
									options={meanComparisonOptions ?? []}
									value={pending.meanComparisonOptionId ?? undefined}
									onChange={(_event, { value }) => changeSettings({ meanComparisonOptionId: value as string })}
								/>
								<DropdownWithLabel
									label={<>&alpha; Value</>}
									loading={alphaOptions === undefined}
									disabled={controlsDisabled}
									options={alphaOptions ?? []}
									value={pending.alphaOptionId ?? undefined}
									onChange={(_event, { value }) => changeSettings({ alphaOptionId: value as string })}
								/>
							</>
						)}
						<ToggleWithLabel
							id="remove-exclusions-toggle"
							label="Remove Exclusions"
							info={removeExclusionsInfo}
							disabled={controlsDisabled}
							checked={pending.removeDamagedOrExcluded}
							onChange={(_event, { checked }) => changeSettings({ removeDamagedOrExcluded: !!checked })}
						/>
						{!isPiano && (
							<>
								<DropdownWithLabel
									label="Trial Filters"
									style={{ maxWidth: 250 }}
									loading={trialFiltersLoading}
									disabled={controlsDisabled}
									options={trialFilterOptions}
									value={display.trialFilterId ?? undefined}
									// The page owns applying this: matching a filter needs each trial's
									// quality/status/response ids, which only the page has fetched.
									onChange={(_event, { value }) => {
										const option = _.find(trialFilterOptions, ["value", value]);
										changeDisplaySettings({
											trialFilterId: value as string,
											trialFilters: option?.filters ?? display.trialFilters,
										});
									}}
								/>
								<ToggleWithLabel
									id="collapse-table"
									label="Collapsed View"
									checked={display.collapseView}
									onChange={(_, { checked }) => {
										changeDisplaySettings({ collapseView: !!checked });
									}}
								/>
							</>
						)}
						<Button
							style={{ alignSelf: "flex-end" }}
							color={!isDirty ? "blue" : undefined}
							negative={isDirty}
							content="Update Analytics"
							disabled={controlsDisabled || display.isEditingMetaTags}
							loading={loading}
							onClick={() => {
								if (!isDirty) return;
								if (onCommit) onCommit();
								else commitSettings();
							}}
						/>
					</div>
				</Accordion.Title>
				{isTimecourse && (
					<Accordion.Content active={accordionActive} style={{ padding: "0 0 6px 22.5px", marginLeft: "16px" }}>
						<div style={{ display: "flex", gap: "16px" }}>
							<DropdownWithLabel
								label="Analysis Type"
								loading={analysisTypeOptions === undefined}
								disabled={controlsDisabled}
								options={analysisTypeOptions ?? []}
								value={pending.analysisTypeId ?? undefined}
								onChange={(_event, { value }) => handleTimecourseSelectionChange({ analysisTypeId: value as string })}
							/>
							<DropdownWithLabel
								label="Ortho"
								loading={orthoOptions === undefined}
								disabled={controlsDisabled}
								options={orthoOptions ?? []}
								value={selectedOrthoImageTypeId ?? ""}
								onChange={(_event, { value }) => handleTimecourseSelectionChange({ orthoImageTypeId: value as string })}
							/>
							<DropdownWithLabel
								label="Quantified Region"
								loading={quantifiedRegionDropdownOptions === undefined}
								disabled={controlsDisabled}
								options={quantifiedRegionDropdownOptions ?? []}
								value={pending.quantifiedRegionTypeId ?? undefined}
								onChange={(_event, { value }) =>
									handleTimecourseSelectionChange({ quantifiedRegionTypeId: value as string })
								}
							/>
						</div>
					</Accordion.Content>
				)}
			</Accordion>
		</div>
	);
};

export default ProtocolAnalysisTopBar;
